import {FC, useEffect, useRef} from 'react';

import {useCallAsChat} from '../ChatView/hooks';
import {Chat} from '../ChatView/types';
import {TraceCallSchema} from '../wfReactInterface/traceServerClientTypes';

export interface CallChatLoaderProps {
  call: TraceCallSchema;
  onChatLoaded?: (call: TraceCallSchema, chat: Chat) => void;
}

/**
 * CallChatLoader triggers useCallAsChat and reports processed chat data to parent.
 *
 * This component enables the fan-out loading pattern where multiple useCallAsChat hooks
 * run concurrently to process calls into chat format in parallel. It renders no UI,
 * only triggering the hook and reporting the converted chat data once per call.
 *
 * @param call - The trace call to convert to chat format
 * @param onChatLoaded - Callback fired when chat conversion is complete (once per call)
 *
 * @example
 * <CallChatLoader
 *   call={traceCall}
 *   onChatLoaded={(call, chat) => console.log('Chat processed for', call.id)}
 * />
 */
export const CallChatLoader: FC<CallChatLoaderProps> = ({
  call,
  onChatLoaded,
}) => {
  const chatData = useCallAsChat(call);
  const hasReportedRef = useRef(false);

  useEffect(() => {
    // Report chat data only when fully loaded and not previously reported
    if (!chatData.loading && chatData.request && !hasReportedRef.current) {
      hasReportedRef.current = true;
      onChatLoaded?.(call, chatData);
    }
  }, [call, chatData, onChatLoaded]);

  // Reset reporting flag when call changes
  useEffect(() => {
    hasReportedRef.current = false;
  }, [call.id]);

  return null;
};
